// src/layouts/openPages/live/LiveApis.js
import axios from 'axios';
import { vendorAuthApi, vendorThingApi, vendorThingUpdatesApi } from '../../../config';

const instance = axios.create({
  timeout: 36000,
});

let authParams = new URLSearchParams(window.location.search);
if (authParams.get('c')) {
  authParams.set('trackingCode', authParams.get('c'));
}

let session = null;
let thingModel = null;
let thingUpdateCallback = null;
let thingUpdateError = null;
let updateCallback = null;
let updateLoop = null;
let motionLoop = null;
let previousLocation = null;
let currentLocation = null;
let nextLocation = null;

// GET THING (initial load)
export function getThing(callback, error) {
  thingUpdateCallback = callback;
  thingUpdateError = error;

  return instance
    .post(vendorAuthApi, Object.fromEntries(authParams))
    .then((response) => {
      session = response.data.session;
      loadThing(callback, error);
    })
    .catch((err) => {
      error(err.response?.data || err.message);
    });
}

function loadThing(callback, error) {
  return instance
    .get(vendorThingApi, { headers: { 'x-dhundhoo-session': session } })
    .then((response) => {
      if (response.data.journeyAvailable === false) {
        error('The bus is in parking. You will be able to track the bus when the trip starts.');
      } else {
        thingModel = response.data;
        callback(response.data);
      }
    })
    .catch((err) => {
      error(err.response?.data || err.message);
    });
}

// START REAL-TIME UPDATES (every 4 seconds)
export function startThingUpdates(callback) {
  updateCallback = callback;
  if (updateLoop) clearInterval(updateLoop);

  // Immediate first call
  getThingUpdates();

  // Then every 4 seconds
  updateLoop = setInterval(() => {
    if (document.visibilityState !== 'hidden') {
      getThingUpdates();
    }
  }, 4000);

  return () => {
    if (updateLoop) clearInterval(updateLoop);
  };
}

function getThingUpdates() {
  return instance
    .get(vendorThingUpdatesApi, { headers: { 'x-dhundhoo-session': session } })
    .then((response) => {
      previousLocation = currentLocation;
      currentLocation = nextLocation;
      nextLocation = response.data.latest.GPS.location;

      if (thingModel && thingModel.journeyAvailable === response.data.journeyAvailable) {
        // Mutate thingModel to avoid re-renders
        Object.assign(thingModel, {
          journeyAvailable: response.data.journeyAvailable,
          lastLocationIndex: response.data.lastLocationIndex,
          nextLocationIndex: response.data.nextLocationIndex,
          lastVisitedStopIndex: response.data.lastVisitedStopIndex,
          updateMessage: response.data.updateMessage,
          latest: response.data.latest,
        });

        // Only call for location update
        if (updateCallback && nextLocation) {
          updateCallback(nextLocation);
        }
        motion();
      } else {
        // Reload full thing only if necessary
        getThing(thingUpdateCallback, thingUpdateError);
      }
    })
    .catch((err) => {
      console.warn('Update failed:', err.message);
    });
}

// SMOOTH MOTION INTERPOLATION (progress-based)
function motion() {
  if (motionLoop) clearInterval(motionLoop);

  let steps = 0;
  const totalSteps = 40; // 4 sec / 100ms = 40 steps

  motionLoop = setInterval(() => {
    if (!previousLocation || !nextLocation || !currentLocation || !updateCallback) {
      clearInterval(motionLoop);
      return;
    }

    if (steps >= totalSteps) {
      clearInterval(motionLoop);
      currentLocation = nextLocation;
      updateCallback(currentLocation);
      return;
    }

    const progress = steps / totalSteps;
    currentLocation = {
      latitude: previousLocation.latitude + (nextLocation.latitude - previousLocation.latitude) * progress,
      longitude: previousLocation.longitude + (nextLocation.longitude - previousLocation.longitude) * progress,
    };

    updateCallback(currentLocation);
    steps++;
  }, 100);
}

// STOP UPDATES
export function stopUpdates() {
  if (updateLoop) clearInterval(updateLoop);
  if (motionLoop) clearInterval(motionLoop);
  updateCallback = null;
  updateLoop = null;
  motionLoop = null;
}